import React, { useId, useRef } from "react";
import {
  FieldLabel,
  FieldRoot,
  FileButton,
  HelperText,
  HiddenFileInput,
  RequiredMark,
} from "./FileUploadField.styles";
import type { FileUploadFieldProps } from "./FileUploadField.types";

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  label,
  required,
  helperText,
  errorMessage,
  className,
  accept,
  selectedFileName,
  buttonLabel = "Upload file",
  multiple = false,
  disabled = false,
  onFileSelected,
}) => {
  const fallbackId = useId();
  const fieldId = `fileuploadfield-${fallbackId}`;
  const helperId = `${fieldId}-helper`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasError = Boolean(errorMessage);

  return (
    <FieldRoot className={className}>
      <FieldLabel htmlFor={fieldId}>
        {label}
        {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
      </FieldLabel>
      <HiddenFileInput
        id={fieldId}
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          Array.from(event.target.files ?? []).forEach((file) => onFileSelected(file));
          event.target.value = "";
        }}
      />
      <FileButton
        type="button"
        $invalid={hasError}
        aria-describedby={helperText || errorMessage ? helperId : undefined}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        title={selectedFileName ?? buttonLabel}
      >
        {selectedFileName || buttonLabel}
      </FileButton>
      {(errorMessage || helperText) && (
        <HelperText id={helperId} $error={hasError} role={hasError ? "alert" : undefined}>
          {errorMessage || helperText}
        </HelperText>
      )}
    </FieldRoot>
  );
};

export default FileUploadField;
